// @flow
import React from 'react';
import { connect } from 'react-redux';
import Image from '../util/Image';
import HeroSliderItemLink from './HeroSliderItemLink';
import { CircularPlayButton } from '../common/CircularPlayButton';
import { ENTITY_TYPE_PLAYLIST, ENTITY_TYPE_ALBUM } from '../../constants';
import { PLAYABLE_ITEM_PREVIEW_DIMS } from '../../lib/imgix';
import styles from './HeroSliderItem.css';

import {
  selectPlayableItemPreviewIsLoadingToPlay,
  selectPlayableItemPreviewIsPlaying,
} from '../../selectors/playableItemPreview';
import collectibleEntity from '../../hoc/collectibleEntity';
import CollectionButton from '../common/CollectionButton';

import * as playableItemPreviewActions from '../../actions/playableItemPreview';
import * as playlistActions from '../../actions/playlist';
import * as albumActions from '../../actions/album';
import { commonMessages } from '../messages/Common';
import { injectIntl } from 'react-intl';
import { compose } from 'redux';
import type { IntlShape } from 'react-intl';

type OwnProps = {
  item: Object,
  isActive: boolean,
  onClick: Function,
  beforeTogglePlay: Function,
  cancelAutoSlide: Function,
  onClick: Function,
  url: string,
  groupId: string,
  isInCollection: boolean,
  toggleIsInCollection: Function,
};

type MapStateToProps = {
  isPlaying: boolean,
  isLoading: boolean,
};

type DispatchProps = {
  togglePlay: Function,
  loadPlaylist: Function,
  loadAlbum: Function,
};

type Props = OwnProps & MapStateToProps & DispatchProps & { intl: IntlShape };

function PlayableHeroSliderItem({
  isActive,
  url,
  item,
  isPlaying,
  isLoading,
  togglePlay,
  onClick,
  beforeTogglePlay,
  cancelAutoSlide,
  groupId,
  intl,
  isInCollection,
  toggleIsInCollection,
  loadPlaylist,
  loadAlbum,
}: Props) {
  const { title, subtitle, imageUrl, imageCredits } = item;

  const onTogglePlay = playing => {
    beforeTogglePlay(item, playing, groupId);
    cancelAutoSlide();
    return togglePlay(item, Date.now());
  };

  const toggleInCollection = async event => {
    event.preventDefault();
    await (item.content.type === ENTITY_TYPE_PLAYLIST
      ? loadPlaylist(item.content.slug)
      : loadAlbum(item.content.slug));
    toggleIsInCollection();
  };

  const showCollectionButton =
    item.content.type === ENTITY_TYPE_PLAYLIST || item.content.type === ENTITY_TYPE_ALBUM;

  return (
    <div className={styles.slideContent}>
      <figure className={styles.imageContainer} role="none">
        <HeroSliderItemLink
          onClick={onClick}
          isActive={isActive}
          url={url}
          title={intl.formatMessage(commonMessages.view, { title })}
        >
          <Image
            alt=""
            src={imageUrl}
            className={styles.image}
            width={PLAYABLE_ITEM_PREVIEW_DIMS.w}
            height={PLAYABLE_ITEM_PREVIEW_DIMS.h}
          />
          {imageCredits && (
            <figcaption className={styles.credits}>Photo: {imageCredits}</figcaption>
          )}
        </HeroSliderItemLink>
        <div className={styles.play}>
          <CircularPlayButton
            onClick={onTogglePlay}
            tabIndex={isActive ? undefined : -1}
            playing={isPlaying}
            loading={isLoading}
            light
          />
        </div>
      </figure>
      <div className={styles.description}>
        <HeroSliderItemLink onClick={onClick} isActive={isActive} url={url}>
          <div className={styles.titleContainer}>
            <p className={styles.title}>{title}</p>
            {showCollectionButton && (
              <CollectionButton active={isInCollection} onClick={toggleInCollection} />
            )}
          </div>
          <p className={styles.subtitle}>{subtitle}</p>
        </HeroSliderItemLink>
      </div>
    </div>
  );
}

function mapStateToProps(state: Object, ownProps: OwnProps): MapStateToProps {
  const item = ownProps.item;

  let normalizedItem;
  if (item.content.type === ENTITY_TYPE_PLAYLIST || item.content.type === ENTITY_TYPE_ALBUM) {
    normalizedItem = {
      type: item.content.type,
      id: item.content.id,
      slug: item.content.slug,
      title: item.title,
      imageUrl: item.imageUrl,
    };
  }

  return {
    isPlaying: selectPlayableItemPreviewIsPlaying(state, item),
    isLoading: selectPlayableItemPreviewIsLoadingToPlay(state, item),
    album: normalizedItem,
    playlist: normalizedItem,
  };
}

const dispatchProps: DispatchProps = {
  togglePlay: playableItemPreviewActions.togglePlayPlayableItemPreview,
  loadPlaylist: playlistActions.loadPlaylist,
  loadAlbum: albumActions.loadAlbum,
};

function getCollectibleEntityDescription(props) {
  return {
    id: props.item.content.id,
    trackingSource: props.item.content.type === ENTITY_TYPE_PLAYLIST ? 'Playlist' : 'Album',
  };
}

export default compose(
  connect(mapStateToProps, dispatchProps),
  injectIntl,
  collectibleEntity(props => props.item.content.type, getCollectibleEntityDescription)
)(PlayableHeroSliderItem);
